import PageSearch from '../../../molecules/PageSearch';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PageList from '../../../templates/PageList';
import ASearch from '../../../organisms/ASearch';
import { storage } from '../../../../utils/storage';
import NewTooltip from '../../../atoms/NewTooltip';
import { IoDocumentText } from "react-icons/io5";
import ProfileBadge from '../../../atoms/ProfileBadge';
import Tooltip from '../../../atoms/Tooltip';
import { TiEdit } from 'react-icons/ti';
import { useState, useContext } from 'react';
import appConfig from '../../../../config/app-config';
import { AuthContext } from '../../../../services/Auth/AuthContext';
import { canEditPerson } from '../../../../services/Auth/roles';

type Profile = {
    id: number;
    type: string;
    verified_level: number;
}

type Address = {
    street_type: string;
    street: string;
    street_number: string;
    neighborhood: string;
    complement: string;
    zipcode: string;
    city_id: string;
    is_active: string;
}

type Contact = {
    id: number;
    type: string;
    content: string;
    is_active: boolean;
}

type Individual = {
    id: number;
    uuid: string;
    name: string;
    socialId: string;
    socialIdFormatted: string;
    birthday: string[];
    maritalState: number;
    gender: string;
    isCountryman: string;
    PEP: boolean;
    profiles: Profile[];
    contacts: Contact[];
    addresses: Address[];

    canEdit: boolean;
    canChangeProfile: boolean;
}

const { address: BASE_URL, key: API_KEY } = appConfig.apis.personApiEnv;

function IndividualPageList() {

    const profile_order = {
        "BORROWER": 1,
        "GUARANTOR": 2,
        "ISSUER": 3,
        "SIGNATORY": 4,
        "CONSENTING_PARTY": 5,
        "RECIPIENT": 6,
        "FINANCIAL_REPRESENTATIVE": 7,
    };


    const navigate = useNavigate();

    const user = useContext(AuthContext)?.user ?? null;
    const canWrite = canEditPerson(user);

    const { page, pageSize, field: fieldSorted, order: orderSorted, filter }: any = useParams();

    // Debug: verificar se params estão sendo recebidos
    console.log('🔍 IndividualPageList params:', { page, pageSize, fieldSorted, orderSorted, filter });

    // Inicializar filtros com dados persistidos
    const persistedFilters = storage.getIndividualFilters();
    let [txtNameSocialId, setTxtNameSocialId] = useState(persistedFilters.txtNameSocialId);
    let [txtEmail, setTxtEmail] = useState(persistedFilters.txtEmail);
    let [txtMobile, setTxtMobile] = useState(persistedFilters.txtMobile);

    let paramNameSocialId = '';
    let paramEmail = '';
    let paramMobile = '';

    if (filter) {
        const matches = filter.match(/=(.*?);/g);
        if (matches && matches.length >= 3) {
            [paramNameSocialId = '', paramMobile = '', paramEmail = ''] = matches
                .map((match: string | any[]) => match.slice(1, -1));
        }
    }

    const url = `${BASE_URL}/individual/page-list?name=${paramNameSocialId}&email=${paramEmail}&mobile=${paramMobile}`
        + `&page=${page}&size=${pageSize}&sort=${fieldSorted},${orderSorted}`;


    const getActiveEmail = (contacts: Contact[]) => {
        const activeEmail = contacts.find((contact: Contact) => contact.type === 'EMAIL' && contact.is_active);
        return activeEmail ? activeEmail.content : '';
    }

    const getActiveMobile = (contacts: Contact[]) => {
        const activeMobile = contacts.find((contact: Contact) => contact.type === 'MOBILE' && contact.is_active);
        return activeMobile ? activeMobile.content : '';
    }

    const getProfiles = (individual: Individual) => {
        return individual.profiles
            .sort((a: Profile, b: Profile) => profile_order[a.type as keyof typeof profile_order] - profile_order[b.type as keyof typeof profile_order])
            .map(profile => profile.type);
    }

    const renderRows = (rows: Individual[]) => {
        if (!rows || rows.length === 0) {
            return (
                <tr>
                    <td colSpan={9} className="py-2 px-2 text-center">
                        <div className="text-2xl font-bold text-gray-500 mt-4">
                            Nenhum registro encontrado...
                        </div>
                    </td>
                </tr>
            );
        }
        return rows.map((row: Individual, index: number) => (
            <tr key={row.id}
                className={`group ${index % 2 === 0 ? 'bg-slate-100' : ''} hover:bg-slate-300 hover:shadow-md border-b border border-gray-300`}>
                <td className="py-2 px-4">{row.id}</td>
                <td className="py-2 px-4 max-w-0 truncate">
                    <Tooltip content={row.name} alignment='bottom'>
                        <span className="block truncate">{row.name}</span>
                    </Tooltip>
                </td>
                <td className="py-2 px-4 max-w-0 truncate">
                    <Tooltip content={row.socialId} alignment='bottom'>
                        <span className="block truncate">{row.socialId}</span>
                    </Tooltip>
                </td>
                <td className="py-2 px-4 whitespace-nowrap">{getActiveEmail(row.contacts)}</td>
                <td className="py-2 px-4 whitespace-nowrap">{getActiveMobile(row.contacts)}</td>
                <td className="py-2 px-4">
                    {row.profiles
                        .slice()
                        .sort((a: Profile, b: Profile) => profile_order[a.type as keyof typeof profile_order] - profile_order[b.type as keyof typeof profile_order])
                        .map((profile: Profile, profIndex: number) => (
                            <span className='mr-2' key={`span-${index}-rp-${profIndex}`}>
                                <ProfileBadge key={`badge-${index}-rp-${profIndex}`} profile={profile} />
                            </span>
                        ))}
                </td>

                <td className="py-2 px-4">
                    <div className="opacity-0 group-hover:opacity-100 flex items-center transition-opacity duration-200">
                        {(canWrite && row.canEdit) && (
                            <NewTooltip content="Ver/editar registro">
                                <Link
                                    id={`btn-copy-${row.uuid}`}
                                    to={`/person/individual/edit/${row.uuid}?tab=0`}
                                    state={{ fromDirectEdit: true }}
                                // onClick={(e) => { e.preventDefault(); alert('Ação de editar registro'); }}
                                >
                                    <TiEdit className='text-2xl text-2xl text-blue-500' />
                                </Link>
                            </NewTooltip>
                        )}

                        {(canWrite && row.canChangeProfile) && (
                            <NewTooltip content="Cadastrar novo perfil">
                                <Link
                                    id={`link-alias-id-${row.id}`}
                                    to={`profiles/${row.uuid}?personType=individual&profiles=${getProfiles(row)}&socialId=${row.socialId}&name=${encodeURIComponent(row.name)}`}
                                >
                                    <IoDocumentText className='ml-2 text-2xl text-blue-500' />
                                </Link>
                            </NewTooltip>
                        )}
                    </div>
                </td>
            </tr >
        ));
    };

    const onFilterClean = () => {
        setTxtNameSocialId('');
        setTxtEmail('');
        setTxtMobile('');

        // Limpar filtros persistidos
        storage.setIndividualFilters({
            txtNameSocialId: '',
            txtEmail: '',
            txtMobile: ''
        });

        navigate(urlNavigate(0, pageSize, fieldSorted, orderSorted, '', '', ''), { replace: true });
    };

    const pageChanged = (page: number) => {
        page = page - 1;
        navigate(urlNavigate(page, pageSize, fieldSorted, orderSorted, txtNameSocialId, txtMobile, txtEmail), { replace: true });
    };

    const pageSizeChanged = (pageSize: number) => {
        navigate(urlNavigate(0, pageSize, fieldSorted, orderSorted, txtNameSocialId, txtMobile, txtEmail), { replace: true });
    };

    const pageSorted = (field: keyof Individual | string, order: 'ASC' | 'DESC') => {
        // Salvar estado da listagem no storage
        storage.setIndividualListState({
            page: 0, // Reset para primeira página ao ordenar
            pageSize: parseInt(pageSize),
            field: field.toString(),
            order
        });

        navigate(urlNavigate(0, pageSize, field, order, txtNameSocialId, txtMobile, txtEmail), { replace: true });
    };

    const nameSocialIdSearch = (search: string) => {
        // Salvar filtros no storage
        storage.setIndividualFilters({
            txtNameSocialId: search,
            txtEmail,
            txtMobile
        });

        navigate(urlNavigate(0, pageSize, fieldSorted, orderSorted, search, txtMobile, txtEmail), { replace: true });
    };

    const emailSearch = (search: string) => {
        // Salvar filtros no storage
        storage.setIndividualFilters({
            txtNameSocialId,
            txtEmail: search,
            txtMobile
        });

        navigate(urlNavigate(0, pageSize, fieldSorted, orderSorted, txtNameSocialId, txtMobile, search), { replace: true });
    };

    const mobileSearch = (search: string) => {
        // Salvar filtros no storage
        storage.setIndividualFilters({
            txtNameSocialId,
            txtEmail,
            txtMobile: search
        });

        navigate(urlNavigate(0, pageSize, fieldSorted, orderSorted, txtNameSocialId, search, txtEmail), { replace: true });
    };

    const urlNavigate = (page: number, pageSize: number, fieldSorted: string, orderSorted: string,
        nameAndSocialId: string, mobile: string, email: string) => {
        return `/individual/individual-list/${page}/${pageSize}/${fieldSorted}/${orderSorted}/p=${nameAndSocialId};r=${mobile};s=${email};`;
    };

    return (

        <PageList
            pageTitle="Listagem de pessoas físicas"
            url={url}
            breadcrumb={[{ label: 'Listagem de pessoas físicas', url: '#' }]}
            fetchHeaders={[{ key: 'x-api-key', value: API_KEY }]}
            tableFieldSort={fieldSorted}
            tableOrderSort={orderSorted}
            onTableSort={pageSorted}
            renderRows={renderRows}
            page={page}
            pageSize={pageSize}
            onPageChange={pageChanged}
            onItemsPerPageChange={pageSizeChanged}
            defaultBackendHeader={false}
            tableHeaders={[
                { id: "column-1", field: 'id', title: '#', canSort: true, width: "80px" },
                { id: "column-3", field: 'name', title: 'Nome', canSort: true },
                { id: "column-4", field: 'socialId', title: 'CPF', canSort: true, width: "150px" },
                { id: "column-4", field: 'email', title: 'Email', canSort: true, width: "150px" },
                { id: "column-4", field: 'mobile', title: 'Celular', canSort: true, width: "150px" },
                { id: "column-5", field: 'profiles', title: 'Perfis', canSort: false, width: "150px" },
                { id: "column-7", field: 'actions', title: 'Ações', canSort: false, width: "100px" },
            ]}
            itemsLabel='pessoas fisicas'
        >
            {{
                filterSlot: (
                    <div className="grid grid-cols-3 gap-1">
                        <ASearch
                            id="src-individual-name"
                            value={txtNameSocialId}
                            label="Procurar por Nome ou CPF..."
                            onChange={(value: string) => setTxtNameSocialId(value)}
                            onSearch={nameSocialIdSearch}
                        />

                        <ASearch
                            id="src-individual-email"
                            value={txtEmail}
                            label="Procurar por Email..."
                            onChange={(value: string) => setTxtEmail(value)}
                            onSearch={emailSearch}
                        />
                        <PageSearch
                            value={txtMobile}
                            label="Procurar por Celular..."
                            onSearch={mobileSearch}
                            onChange={(value: string) => setTxtMobile(value)}
                            filterCleanVisible={true}
                            filterCleanEnabled={(txtNameSocialId || txtEmail || txtMobile) ? true : false}
                            configureVisible={false}
                            onFilterClean={onFilterClean}
                        />
                    </div>
                ),
                controlSlot: canWrite ? (
                    <div className='pt-2' >
                        <NewTooltip content='Cadastrar nova pessoa' alignment='left'>
                            <Link id="link-new-individual" to='/person/individual/form'
                                className='text-white bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm px-5 py-2.5 text-center'
                            >
                                Novo cadastro
                            </Link>
                        </NewTooltip>
                    </div>
                ) : null,
            }}
        </PageList >
    );
};

export default IndividualPageList;